import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { DashboardService} from "../../../../services/dashboard/dashboardService";
import { PickupDonut } from "../../components/pickups/PickupDonut";
import { PickupCompletionRatio } from "../../components/pickups/PickupCompletionRatio";
import { TotalOilCollected } from "../../components/pickups/TotalOilCollected";
import { OilCollectionRanking } from "../../components/pickups/HeadquarterOilCollecionRanking";
import type { DateRange } from "../utils/date";


const fadeUp = {
    hidden: { opacity: 0, y: 12 },
    visible: (i = 1) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06 } }),
};


interface Props {
    range: DateRange;
    refreshKey: number; // para forzar recargas manuales
}


export const PickupsStatsTab: React.FC<Props> = ({ range, refreshKey }) => {
    // Estados locales del tab
    const [pickupStats, setPickupStats] = useState<{ completed: number; failed: number } | null>(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [errorStats, setErrorStats] = useState<string | null>(null);


    const [ratioData, setRatioData] = useState<{ completed: number; planned: number } | null>(null);
    const [ratioLoading, setRatioLoading] = useState(false);
    const [ratioError, setRatioError] = useState<string | null>(null);


    const [oilTotal, setOilTotal] = useState<number>(0);
    const [oilSeries, setOilSeries] = useState<{ date: string; value: number }[]>([]);
    const [oilLoading, setOilLoading] = useState(false);
    const [oilError, setOilError] = useState<string | null>(null);


    const [oilRanking, setOilRanking] = useState<any[]>([]);
    const [oilRankingLoading, setOilRankingLoading] = useState(false);
    const [oilRankingError, setOilRankingError] = useState<string | null>(null);


    // Fetchers
    const fetchPickupStats = async (r: DateRange) => {
        try {
            setLoadingStats(true);
            setErrorStats(null);
            const service = new DashboardService();
            const res = await service.satisfactoryAndUnsatisfactoryPickups({ time: { start: r.start, end: r.end } });
            const body = (res as any)?.response?.data;
            if (body?.success) setPickupStats({ completed: body.data?.completed ?? 0, failed: body.data?.failed ?? 0 });
            else setPickupStats({ completed: 0, failed: 0 });
        } catch (e: any) {
            setErrorStats(e?.message || "Error obteniendo estadísticas");
            setPickupStats(null);
        } finally {
            setLoadingStats(false);
        }
    };

    const fetchCompletionRatio = async (r: DateRange) => {
        try {
            setRatioLoading(true);
            setRatioError(null);
            const service = new DashboardService();
            const res = await service.completedVsPlannedPickupsPercentage({ time: { start: r.start, end: r.end } });
            const body = (res as any)?.response?.data;
            if (body?.success) setRatioData({ completed: body.data?.completed ?? 0, planned: body.data?.planned ?? 0 });
            else setRatioData({ completed: 0, planned: 0 });
        } catch (e: any) {
            setRatioError(e?.message || "Error obteniendo porcentaje completadas vs planificadas");
            setRatioData(null);
        } finally {
            setRatioLoading(false);
        }
    };

    const fetchOilTotal = async (r: DateRange) => {
        try {
            setOilLoading(true);
            setOilError(null);
            const service = new DashboardService();
            const res = await service.totalOilVolumeCollected({ time: { start: r.start, end: r.end } });
            const body = (res as any)?.response?.data;
            if (body?.success) {
                setOilTotal(parseFloat(body.data?.totalOilCollectedKg ?? "0") || 0);
                setOilSeries([]);
            } else {
                setOilTotal(0);
                setOilSeries([]);
            }
        } catch (e: any) {
            setOilError(e?.message || "Error obteniendo aceite recolectado");
            setOilTotal(0);
            setOilSeries([]);
        } finally {
            setOilLoading(false);
        }
    };

    const fetchOilRanking = async (r: DateRange) => {
        try {
            setOilRankingLoading(true);
            setOilRankingError(null);
            const service = new DashboardService();
            const res = await service.oilCollectedRankingByHeadquarter({ time: { start: r.start, end: r.end } });
            const body = (res as any)?.response?.data;
            if (body?.success && Array.isArray(body.data)) setOilRanking(body.data);
            else setOilRanking([]);
        } catch (e: any) {
            setOilRankingError(e?.message || "Error obteniendo el ranking por sede");
            setOilRanking([]);
        } finally {
            setOilRankingLoading(false);
        }
    };


    const fetchBundle = (r: DateRange) => {
        fetchPickupStats(r);
        fetchCompletionRatio(r);
        fetchOilTotal(r);
        fetchOilRanking(r);
    };

    // Dispara al cambiar rango o forzar refresh
    useEffect(() => {
        fetchBundle(range);
    }, [range.start, range.end, refreshKey]);

    return (
        <>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-stretch">
                <motion.div variants={fadeUp} initial="hidden" animate="visible" className="h-full">
                    <PickupDonut
                        periodLabel={`Periodo: ${range.start} → ${range.end}`}
                        completed={pickupStats?.completed ?? 0}
                        failed={pickupStats?.failed ?? 0}
                        loading={loadingStats}
                        error={errorStats}
                        colors={["#326e5a", "#c51b1b"]}
                        height={280}
                        className="h-full"
                    />
                </motion.div>


                <motion.div variants={fadeUp} initial="hidden" animate="visible" className="h-full">
                    <TotalOilCollected
                        periodLabel={`Periodo: ${range.start} → ${range.end}`}
                        totalKg={oilTotal}
                        series={oilSeries}
                        loading={oilLoading}
                        error={oilError}
                        height={280}
                    />
                </motion.div>
            </div>


            <motion.div variants={fadeUp} initial="hidden" animate="visible" className="h-full">
                <PickupCompletionRatio
                    periodLabel={`Periodo: ${range.start} → ${range.end}`}
                    completed={ratioData?.completed ?? 0}
                    planned={ratioData?.planned ?? 0}
                    loading={ratioLoading}
                    error={ratioError}
                    height={280}
                    className="h-full"
                />
            </motion.div>


            <motion.div variants={fadeUp} initial="hidden" animate="visible" className="h-full">
                <OilCollectionRanking
                    periodLabel={`Periodo: ${range.start} → ${range.end}`}
                    items={oilRanking}
                    loading={oilRankingLoading}
                    error={oilRankingError}
                    height={320}
                />
            </motion.div>
        </>
    );
};